import { Checkbox, FormControlLabel, FormGroup, IconButton, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TablePaginationActions, TableRow } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { TableHeader } from '../../app/AppStyles';
import { GetDate, GetTime, SearchKeyFilter } from "../../app/AppUtils";
import BackButton from "../subcomponents/BackButton";
import ContentLoader from "../subcomponents/ContentLoader";
import FilterSidebar from "../subcomponents/FilterSidebar";
import PageLayout from "../subcomponents/PageLayout";
import { deleteAttendances, fetchMomentAttendance, fetchPerformanceAttendance, resetAttendancesDelete } from "./attendanceSlice";

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationModal from "../subcomponents/ConfirmationModal";
import { setAlertMessage, setAlertSeverity, setAlertStatus } from "../subcomponents/alertSlice";

export default function AttendancePerformancePage() {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { attendancesPerformance, attendancesDelete } = useSelector((state) => state.attendance);
    const { aspNetUser } = useSelector((state) => state.settings.app);

    const [filter, setFilter] = useState("");
    const [completed, setCompleted] = useState(false);

    useEffect(() => {
        dispatch(fetchPerformanceAttendance(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (attendancesDelete.status === "success") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.DELETE_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            dispatch(fetchPerformanceAttendance(id));
            dispatch(resetAttendancesDelete());
        } else if (attendancesDelete.status === "failed") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.ERROR_DELETE_FAILED}`));
            dispatch(setAlertSeverity("error"));
            dispatch(resetAttendancesDelete());
        };
    }, [attendancesDelete.status]);

    const handleFilterResult = (item) => {
        return SearchKeyFilter(item, ["activityName", "performanceNumber"], filter) && (item.completed === completed);
    }

    const FilterFragment = () => {
        return (
            <>
                <FormGroup sx={{ width: 1 }}>
                    <FormControlLabel sx={{ alignSelf: 'center' }} control={<Checkbox checked={completed} onChange={() => setCompleted(!completed)} />} label={`${resourceFinal[appLanguage].words.COMPLETED}`} />
                </FormGroup>
            </>
        );
    };

    const TableFragment = () => {
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);

        const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - attendancesPerformance.data?.filter(handleFilterResult).length) : 0;

        const handleChangePage = (event, newPage) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        const attendanceClick = (item) => {
            dispatch(fetchMomentAttendance(item.momentId));
            navigate(`/presentie/moment/${item.momentId}`);
        };

        const handleDeleteClick = (momentId) => {
            dispatch(deleteAttendances(momentId));
        };

        return (
            <>
                <TableBody>
                    {(rowsPerPage > 0 ? attendancesPerformance.data?.filter(handleFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : attendancesPerformance.data?.filter(handleFilterResult)).map(item =>
                        <TableRow key={item.momentId} sx={{ cursor: 'pointer', '&:nth-of-type(odd)': { bgcolor: 'white.main' }, height: 63 }}>
                            <TableCell align="left">{item.activityName}</TableCell>
                            <TableCell align="center">{item.performanceNumber}</TableCell>
                            <TableCell align="center">{item.locationName}</TableCell>
                            <TableCell align="center">{GetDate(item.startTime)} ({GetTime(item.startTime)})</TableCell>
                            <TableCell align="center"><Checkbox disabled checked={item.completed} color="secondary" /></TableCell>
                            {(aspNetUser?.role?.includes("Supervisor") && completed) && <TableCell align="center" padding="checkbox">
                                <ConfirmationModal iconButton icon={<DeleteIcon color="secondary" />} message={`${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_1} ${resourceFinal[appLanguage].words.ATTENDANCE.toLowerCase()} (${GetDate(item.startTime)}) ${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_2}`} confirmAction={() => handleDeleteClick(item.momentId)} />
                            </TableCell>}
                            <TableCell align="center"><IconButton onClick={() => attendanceClick(item)}><ArrowForwardIosIcon color="secondary" /></IconButton></TableCell>
                        </TableRow>
                    )}

                    {(rowsPerPage > 0 ? attendancesPerformance.data?.filter(handleFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : attendancesPerformance.data?.filter(handleFilterResult)).length === 0 && <TableRow><TableCell colSpan={6}>{resourceFinal[appLanguage].messages.FILTER_RESULT_EMPTY}</TableCell></TableRow>}
                    {emptyRows > 0 && <TableRow style={{ height: 63 * emptyRows }}><TableCell colSpan={6} /></TableRow>}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            sx={{ border: 0 }}
                            rowsPerPageOptions={[10, 25, 50,
                                {
                                    label: attendancesPerformance.data?.length < 100 ? resourceFinal[appLanguage].words.ALL : '100',
                                    value: attendancesPerformance.data?.length < 100 ? -1 : 100
                                }
                            ]}
                            count={attendancesPerformance.data?.filter(handleFilterResult)?.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                            labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                        />
                    </TableRow>
                </TableFooter>
            </>
        );
    };

    return (
        <PageLayout
            title={`${resourceFinal[appLanguage].words.ATTENDANCE}`}
            content={
                <ContentLoader
                    successCondition={attendancesPerformance.status === "success"}
                    errorCondition={attendancesPerformance.status === "failed"}
                    content={
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.ACTIVITY}{resourceFinal[appLanguage].words.NAME.toLowerCase()}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.PERFORMANCE}{resourceFinal[appLanguage].words.NUMBER.toLowerCase()}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.LOCATION}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.START}{resourceFinal[appLanguage].words.TIME.toLowerCase()}</TableCell>
                                        <TableCell align="center" padding="checkbox" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.COMPLETED}</TableCell>
                                        {(aspNetUser?.role?.includes("Supervisor") && completed ) && <TableCell align="center" padding="checkbox" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.DELETE}</TableCell>}
                                        <TableCell align="center" padding="checkbox"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableFragment />
                            </Table>
                        </TableContainer>
                    }
                />
            } contentLeft={
                <FilterSidebar enableSearch searchFilter={filter} searchFilterSet={setFilter} filters={<FilterFragment />} />
            } contentRight={
                <BackButton desktop />
            }
        />
    );
}
